import * as React from "react"
import { useLocation } from "react-router-dom"

import { useI18n } from "@/lib/i18n"
import {
  ambitoDeRuta,
  apertura as resolverApertura,
  momento as resolverMomento,
  voz as resolverVoz,
  type Ambito,
  type Idioma,
  type Voz,
} from "@/lib/marty"

/**
 * LA VOZ DE MARTY, CABLEADA A LA PANTALLA QUE SE ESTA MIRANDO.
 *
 * El catalogo vive en `lib/marty.ts` y es codigo puro —se prueba sin montar
 * React—. Esto es solo el cable: toma la RUTA para saber en que seccion esta
 * quien mira, el IDIOMA de la sociedad activa y el NOMBRE DEL FISCO de su pais,
 * y deja la voz resuelta a mano de cualquier componente.
 *
 * Por que un contexto y no un parametro en cada pantalla: `DataState` es el
 * componente que pinta la carga, el vacio y el error en las veintitantas
 * pantallas del producto. Si el ambito viajara como prop habria que tocar las
 * veintitantas —y la numero veintitres, la que alguien escriba el mes que
 * viene, se olvidaria—. Con el contexto, una pantalla nueva habla como Marty
 * por el solo hecho de estar montada bajo el layout.
 *
 * El `useVoz()` FUNCIONA SIN PROVEEDOR a proposito: devuelve la voz generica en
 * espanol. Las pruebas unitarias montan componentes sueltos, sin Router y sin
 * I18nProvider, y un hook que revienta ahi obligaria a envolver cada prueba en
 * dos proveedores para verificar una tabla.
 */

interface VozContextValue {
  ambito: Ambito
  idioma: Idioma
  /** El nombre legal del fisco del pais de la sociedad: SUNAT, SAT, DGI… */
  autoridad: string | null
  voz: Voz
  /** Lo que Marty dice al ABRIR esta seccion. Ver `MartyNota`. */
  apertura: string
  /** Una frase de MOMENTO (primer login, cierre listo, vencimiento…). */
  momento: (clave: string, datos?: { cuando?: string | null }) => string
}

const VozContext = React.createContext<VozContextValue | null>(null)

/** Lo que se devuelve cuando no hay proveedor. Ver el docstring. */
const POR_DEFECTO: VozContextValue = {
  ambito: "generico",
  idioma: "es",
  autoridad: null,
  voz: resolverVoz("generico", "es"),
  apertura: resolverApertura("generico", "es"),
  momento: (clave, datos) => resolverMomento(clave, "es", datos),
}

export function VozProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const i18n = useI18n()

  const idioma = (i18n.idioma ?? "es") as Idioma
  const autoridad = i18n.term?.autoridad ?? null

  const value = React.useMemo<VozContextValue>(() => {
    const ambito = ambitoDeRuta(pathname)
    return {
      ambito,
      idioma,
      autoridad,
      voz: resolverVoz(ambito, idioma, autoridad),
      apertura: resolverApertura(ambito, idioma, autoridad),
      momento: (clave, datos) =>
        resolverMomento(clave, idioma, { autoridad, cuando: datos?.cuando }),
    }
  }, [pathname, idioma, autoridad])

  return <VozContext.Provider value={value}>{children}</VozContext.Provider>
}

export function useVoz(): VozContextValue {
  return React.useContext(VozContext) ?? POR_DEFECTO
}

/**
 * La voz de un ambito CONCRETO, ignorando la ruta.
 *
 * Para los dialogos y los paneles que viven dentro de otra pantalla: el dialogo
 * de asiento de ajuste se abre desde el diario, pero lo que carga es un plan de
 * cuentas, no un diario.
 */
export function useVozDe(ambito: Ambito): Voz {
  const { idioma, autoridad } = useVoz()
  return React.useMemo(() => resolverVoz(ambito, idioma, autoridad), [ambito, idioma, autoridad])
}
